import React from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import { variants as iconVariants } from '../icon/style';
import { BoxProps, Flex } from '../box';
import Icon from '../icon';
import { SquareButton } from '../button';
import { SquareButtonProps } from '../button/square-button';

// constants
import colors from '../../style/colors';
import styleConstants from '../../style/constants';
import HeaderToolbar from './toolbar';
import NavTabButton, { NavTabGroup } from './nav-tab-button';
import AppSwitcher from './app-switcher/app-switcher-components';

const variants = {
  'default-secondary': css`
    background-color: ${colors.white};
    height: ${styleConstants.headerHeight}px;
    z-index: 0;
    border-bottom: 1px solid ${colors.gray300};
  `,
  default: css`
    background-color: ${colors.white};
    z-index: 1100;
    box-shadow:
      0px 2px 4px -1px rgb(0 0 0 / 10%),
      0px 4px 5px 0px rgb(0 0 0 / 6%),
      0px 1px 10px 0px rgb(0 0 0 / 12%);
  `,
};

interface HeaderProps {
  backgroundColor?: string;
  className?: string;
  variant?: keyof typeof variants;
  children?: React.ReactNode;
  componentClass?: 'header' | 'nav';
  position?: 'fixed' | 'relative' | 'static' | 'sticky';
  top?: number;
  paddingHorizontal?: 'none';
}

const HeaderComponent = ({ className, componentClass: Component = 'header', ...rest }: HeaderProps) => {
  return <Component className={classNames(className)} {...rest} />;
};

const StyledHeader = styled(HeaderComponent).withConfig({
  shouldForwardProp: prop => !['backgroundColor', 'paddingHorizontal', 'position', 'top'].includes(prop),
})<HeaderProps>`
  height: ${styleConstants.headerHeight}px;
  box-shadow:
    0px 2px 4px -1px rgb(0 0 0 / 10%),
    0px 4px 5px 0px rgb(0 0 0 / 6%),
    0px 1px 10px 0px rgb(0 0 0 / 12%);
  justify-content: center;
  color: #fff;
  top: ${props => props.top || 0};
  left: auto;
  right: 0;
  width: 100%;
  display: flex;
  z-index: 1100;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-direction: column;
  transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  padding: 8px ${props => props.paddingHorizontal || '24px'};

  ${props => variants[props.variant || 'default']};
  position: ${props => props.position || 'fixed'};
  background-color: ${props => props.backgroundColor};
`;

const Header = ({ ...rest }: HeaderProps) => {
  return <StyledHeader {...rest} />;
};

Header.Toolbar = HeaderToolbar;

Header.MenuToggle = ({
  variant,
  ...rest
}: Omit<SquareButtonProps, 'variant' | 'aria-label'> & { variant?: keyof typeof iconVariants }) => (
  <SquareButton
    data-testid={'header-menu-toggle'}
    variant={'clean'}
    className={'visible-xs'}
    aria-label={'Open menu'}
    {...rest}
  >
    <Icon variant={variant} name={'MENU'} size={'xl'} />
  </SquareButton>
);

Header.ToolbarContainer = ({ height, ...rest }: BoxProps) => (
  <Flex flexGrow={1} justifyContent={'space-between'} height={height} {...rest} />
);

Header.NavTabGroup = NavTabGroup;

Header.NavTab = NavTabButton;

Header.AppSwitcher = AppSwitcher;

export default Header;
